/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { useTheme } from "@mui/material";
import { createContext, FC, ReactNode, useContext, useState } from "react";

export interface IBackgroundContextType {
  backgroundColor: string;
  setBackgroundColor: (color: string) => void;
  resetBackgroundColor: () => void;
}

export interface IBackgroundProviderProps {
  children: ReactNode;
  defaultColor?: string;
}

const BackgroundContext = createContext<IBackgroundContextType | undefined>(undefined);
export const useBackgroundColor = () => {
  const context = useContext(BackgroundContext);
  if (!context) {
    throw new Error("useBackgroundColor must be used within a BackgroundProvider");
  }

  return context;
};

export const BackgroundProvider: FC<IBackgroundProviderProps> = ({
  children,
  defaultColor,
}: IBackgroundProviderProps) => {
  const theme = useTheme();
  const defaultBackgroundColor = defaultColor || theme.palette.common.white;

  const [backgroundColor, setBackgroundColor] = useState(defaultBackgroundColor);

  const resetBackgroundColor = () => setBackgroundColor(defaultBackgroundColor);

  return (
    <BackgroundContext.Provider
      value={{ backgroundColor, setBackgroundColor, resetBackgroundColor }}
    >
      {children}
    </BackgroundContext.Provider>
  );
};
